import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import '../databases/databases.css';
import icon from '../../assets/icon.svg';

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi',
  'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo (Congo-Brazzaville)','Costa Rica','Côte d’Ivoire','Croatia','Cuba','Cyprus','Czechia (Czech Republic)',
  'Democratic Republic of the Congo','Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini (fmr. "Swaziland")','Ethiopia',
  'Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Holy See','Honduras','Hungary',
  'Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan',
  'Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar (formerly Burma)',
  'Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway',
  'Oman',
  'Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal',
  'Qatar',
  'Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria',
  'Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Vanuatu','Venezuela','Vietnam',
  'Yemen',
  'Zambia','Zimbabwe'
];

export default function RentalApplication() {
  const [searchParams] = useSearchParams();
  const propertyIdFromUrl = searchParams.get('propertyId');
  const unitIdFromUrl = searchParams.get('unitId');
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [form, setForm] = useState({
    // Personal
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    ssn: '',
    drivers_license: '',
    current_street: '',
    current_apt: '',
    current_city: '',
    current_state: '',
    current_postal: '',
    current_country: 'United States',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    // Additional occupants / co-signers
    co_applicants: [],
    // Property & Lease
    property_interest: '',
    move_in_date: '',
    lease_length: '',
    occupants: '',
    has_pets: 'no',
    pet_description: '',
    has_vehicles: 'no',
    vehicles: [
      { license_plate: '', make: '', model: '', color: '', year: '' }
    ],
    // Financial & Employment
    monthly_income: '',
    employment_status: '',
    employer_name: '',
    job_title: '',
    employment_length: '',
    supervisor_name: '',
    supervisor_phone: '',
    employment_verification: '',
    // References
    reference1_name: '',
    reference1_phone: '',
    reference1_relationship: '',
    reference2_name: '',
    reference2_phone: '',
    reference2_relationship: '',
    // Previous Rental History
    prev_street: '',
    prev_apt: '',
    prev_city: '',
    prev_state: '',
    prev_postal: '',
    prev_country: 'United States',
    prev_landlord_name: '',
    prev_landlord_phone: '',
    prev_monthly_rent: '',
    reason_for_leaving: '',
    // Legal history
    felony: 'no',
    eviction: 'no',
    bankruptcy: 'no',
    additional_info: '',
    // CRM defaults
        source: 'Listings Website',
    status: 'application',
    lead_score: 85,
  });

  useEffect(() => {
    const fetchProps = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties`);
        if (!res.ok) return;
        const data = await res.json();
        const opts = Array.isArray(data)
          ? data.map(p => ({ id: p.id, name: p.property_name || p.name || `Property ${p.id}` }))
          : [];
        setProperties(opts);
        
        // If propertyId is in URL, pre-select it
        if (propertyIdFromUrl) {
          const property = opts.find(p => p.id.toString() === propertyIdFromUrl);
          if (property) {
            setSelectedProperty(property);
            setForm(prev => ({ ...prev, property_interest: property.name }));
          }
        }
      } catch (_) {}
    };
    fetchProps();
  }, [propertyIdFromUrl]);

  const [paystubFile, setPaystubFile] = useState(null);
  const [bankStatementFile, setBankStatementFile] = useState(null);
  const [creditReportFile, setCreditReportFile] = useState(null);

  const canNext = useMemo(() => {
    if (step === 1) {
      return form.first_name && form.last_name && form.email;
    }
    if (step === 3) {
      return Boolean(paystubFile && bankStatementFile && creditReportFile);
    }
    return true;
  }, [step, form, paystubFile, bankStatementFile, creditReportFile]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // 1) Create base applicant
      const formatAddress = (prefix) => {
        const parts = [
          form[`${prefix}_street`],
          form[`${prefix}_apt`] ? `Apt ${form[`${prefix}_apt`]}` : null,
          form[`${prefix}_city`],
          form[`${prefix}_state`],
          form[`${prefix}_postal`],
          form[`${prefix}_country`]
        ].filter(Boolean);
        return parts.join(', ');
      };

      const createBody = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        source: form.source,
        status: 'application',
        property_interest: form.property_interest,
        budget_min: null,
        budget_max: null,
        move_in_date: form.move_in_date,
        lease_length: form.lease_length,
        notes: [
          form.additional_info || '',
          `Current Address: ${formatAddress('current')}`
        ].filter(Boolean).join('\n'),
        lead_score: form.lead_score,
        assigned_agent: null,
        last_contact: new Date().toISOString().split('T')[0]
      };
      const res = await fetch(`${API_BASE_URL}/api/applicants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createBody)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit application');
      }
      const { id } = await res.json();

      // 2) Update with additional fields supported by backend schema
      const updateBody = {
        employment_status: form.employment_status,
        employer_name: form.employer_name,
        job_title: form.job_title,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        // free-form extras stored in notes append
        notes: [
          form.additional_info || '',
          `Applicants Portal Data:`,
          `DOB: ${form.date_of_birth || ''}`,
          `DL: ${form.drivers_license || ''}`,
          `Current Address: ${formatAddress('current')}`,
          `Emergency: ${form.emergency_contact_name || ''} / ${form.emergency_contact_phone || ''}`,
          form.co_applicants && form.co_applicants.length ? `Additional Occupants: ${form.co_applicants.map(o => `${o.first_name || ''} ${o.last_name || ''} (${o.status || 'occupant'}) ${o.email || ''} ${o.dob || ''}`.trim()).join(' | ')}` : '',
          `Occupants: ${form.occupants || ''}; Pets: ${form.has_pets}${form.has_pets==='yes' && form.pet_description ? ` (${form.pet_description})` : ''}`,
          form.has_vehicles === 'yes' ? `Vehicles: ${form.vehicles.map(v => `${v.license_plate} ${v.make} ${v.model} ${v.color} ${v.year}`.trim()).join(' | ')}` : '',
          `Refs: ${form.reference1_name || ''} (${form.reference1_relationship || ''}) ${form.reference1_phone || ''}; ${form.reference2_name || ''} (${form.reference2_relationship || ''}) ${form.reference2_phone || ''}`,
          `Prev Rental: ${formatAddress('prev')}; Landlord: ${form.prev_landlord_name || ''} ${form.prev_landlord_phone || ''}; Rent: ${form.prev_monthly_rent || ''}`,
          `Employment Verification: ${form.employment_verification || ''}`,
          `Legal: felony=${form.felony}, eviction=${form.eviction}, bankruptcy=${form.bankruptcy}`
        ].filter(Boolean).join('\n')
      };
      await fetch(`${API_BASE_URL}/api/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody)
      });

      // 3) Upload required documents (temporary endpoint)
      const uploadDoc = async (file, description) => {
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        fd.append('description', `${description} (applicant:${id})`);
        try { await fetch(`${API_BASE_URL}/api/test-upload`, { method: 'POST', body: fd }); } catch (_) {}
      };
      await uploadDoc(paystubFile, 'Paystub');
      await uploadDoc(bankStatementFile, 'Bank Statement');
      await uploadDoc(creditReportFile, 'Credit Report');

      setSuccess('Application submitted successfully!');
      setStep(6);
    } catch (e) {
      setError(e.message || 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tenant-edit-page" style={{ background: '#fff', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={icon} alt="RentifAI" style={{ width: 36, height: 36 }} />
            <strong style={{ fontSize: 18, color: '#212529' }}>RentifAI</strong>
          </div>
          <div style={{ padding: '6px 12px', border: '2px dashed #ced4da', borderRadius: 8, color: '#6c757d' }}>
            Your Logo Here
          </div>
        </div>
        <h1 style={{ marginBottom: 8 }}>Rental Application</h1>
        <p style={{ color: '#6c757d', marginBottom: 24 }}>Please fill out all sections completely and accurately</p>

        {/* Simple progress bar */}
        <div style={{ height: 6, background: '#e9ecef', borderRadius: 999, margin: '16px 0 24px' }}>
          <div style={{ height: 6, width: `${(step - 1) / 5 * 100}%`, background: '#0d6efd', borderRadius: 999, transition: 'width .25s' }} />
        </div>

        {error && <div className="error-message" style={{marginBottom:12}}>{error}</div>}
        {success && <div className="success-message" style={{marginBottom:12}}>{success}</div>}

        {/* Step 1: Personal */}
        {step === 1 && (
          <section className="info-section">
            <h2>Personal Information</h2>
            <div className="form-grid">
              <div className="form-field"><label>First Name</label><input className="form-input" value={form.first_name} onChange={e=>update('first_name',e.target.value)} placeholder="Enter first name"/></div>
              <div className="form-field"><label>Last Name</label><input className="form-input" value={form.last_name} onChange={e=>update('last_name',e.target.value)} placeholder="Enter last name"/></div>
              <div className="form-field"><label>Email Address</label><input className="form-input" type="email" value={form.email} onChange={e=>update('email',e.target.value)} placeholder="your.email@example.com"/></div>
              <div className="form-field"><label>Phone Number</label><input className="form-input" value={form.phone} onChange={e=>update('phone',e.target.value)} placeholder="(555) 123-4567"/></div>
              <div className="form-field"><label>Date of Birth</label><input className="form-input" type="date" value={form.date_of_birth} onChange={e=>update('date_of_birth',e.target.value)} /></div>
              <div className="form-field"><label>Driver's License Number</label><input className="form-input" value={form.drivers_license} onChange={e=>update('drivers_license',e.target.value)} placeholder="Enter license number"/></div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Current Address</label>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input className="form-input" placeholder="Street Address" value={form.current_street} onChange={e=>update('current_street',e.target.value)} />
                  <input className="form-input" placeholder="Apt / Unit (optional)" value={form.current_apt} onChange={e=>update('current_apt',e.target.value)} />
                  <input className="form-input" placeholder="City / Town" value={form.current_city} onChange={e=>update('current_city',e.target.value)} />
                  <select className="form-input" value={form.current_country} onChange={e=>update('current_country',e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {(form.current_country === 'United States' || form.current_country === 'Canada') && (
                    <input className="form-input" placeholder={form.current_country === 'Canada' ? 'Province' : 'State'} value={form.current_state} onChange={e=>update('current_state',e.target.value)} />
                  )}
                  <input className="form-input" placeholder="Postal / ZIP Code" value={form.current_postal} onChange={e=>update('current_postal',e.target.value)} />
                </div>
              </div>
              <div className="form-field"><label>Emergency Contact Name</label><input className="form-input" value={form.emergency_contact_name} onChange={e=>update('emergency_contact_name',e.target.value)} /></div>
              <div className="form-field"><label>Emergency Contact Phone</label><input className="form-input" value={form.emergency_contact_phone} onChange={e=>update('emergency_contact_phone',e.target.value)} /></div>
            </div>
            <div className="form-field" style={{ marginTop: 16 }}>
              <h3 style={{ margin: '8px 0' }}>Co-signer / Occupant / Co-leaseholder</h3>
              <div style={{ background:'#f8f9fa', border:'1px solid #e9ecef', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 16, width:'100%', boxSizing:'border-box', overflow:'hidden' }}>
                {(form.co_applicants || []).map((o, idx) => (
                  <div key={idx} style={{ background:'#ffffff', border:'1px solid #e9ecef', borderRadius: 8, padding: 12, width:'100%', boxSizing:'border-box' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(220px, 1fr))', gap: 16, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>First Name</label>
                        <input className="form-input" value={o.first_name || ''} onChange={(e)=>{
                          const next = [...(form.co_applicants || [])];
                          next[idx] = { ...next[idx], first_name: e.target.value };
                          update('co_applicants', next);
                        }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Last Name</label>
                        <input className="form-input" value={o.last_name || ''} onChange={(e)=>{
                          const next = [...(form.co_applicants || [])];
                          next[idx] = { ...next[idx], last_name: e.target.value };
                          update('co_applicants', next);
                        }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Date of Birth</label>
                        <input className="form-input" type="date" value={o.dob || ''} onChange={(e)=>{
                          const next = [...(form.co_applicants || [])];
                          next[idx] = { ...next[idx], dob: e.target.value };
                          update('co_applicants', next);
                        }} />
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(220px, 1fr))', gap: 16 }}>
                      <div>
                        <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Email</label>
                        <input className="form-input" type="email" value={o.email || ''} onChange={(e)=>{
                          const next = [...(form.co_applicants || [])];
                          next[idx] = { ...next[idx], email: e.target.value };
                          update('co_applicants', next);
                        }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Status</label>
                        <select className="form-input" value={o.status || ''} onChange={(e)=>{
                          const next = [...(form.co_applicants || [])];
                          next[idx] = { ...next[idx], status: e.target.value };
                          update('co_applicants', next);
                        }}>
                          <option value="">Select status</option>
                          <option value="co-signer">Co-signer</option>
                          <option value="co-leaseholder">Co-leaseholder</option>
                          <option value="occupant">Occupant</option>
                        </select>
                      </div>
                      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'flex-end' }}>
                        <button className="back-button" onClick={()=>{
                          const next = (form.co_applicants || []).filter((_,i)=>i!==idx);
                          update('co_applicants', next);
                        }}>Remove</button>
                      </div>
                    </div>
                  </div>
                ))}
                <button className="save-button" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => update('co_applicants', [...(form.co_applicants || []), { first_name:'', last_name:'', dob:'', status:'', email:'' }])}>+ Add Occupant/Co-Signer</button>
              </div>
            </div>
          </section>
        )}

        {/* Step 2: Property & Lease */}
        {step === 2 && (
          <section className="info-section">
            <h2>Property & Lease Details</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Property of Interest</label>
                <select className="form-input" value={form.property_interest} onChange={e=>update('property_interest',e.target.value)}>
                  <option value="">Select a property</option>
                  {properties.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Desired Move-in Date</label><input className="form-input" type="date" value={form.move_in_date} onChange={e=>update('move_in_date',e.target.value)} /></div>
              <div className="form-field"><label>Lease Length</label><select className="form-input" value={form.lease_length} onChange={e=>update('lease_length',e.target.value)}><option value="">Select lease length</option><option value="6">6 Months</option><option value="12">12 Months</option><option value="24">24 Months</option></select></div>
              <div className="form-field"><label>Number of Occupants</label><select className="form-input" value={form.occupants} onChange={e=>update('occupants',e.target.value)}><option value="">Select number of occupants</option>{Array.from({length:8},(_,i)=>i+1).map(n=>(<option key={n} value={n}>{n}</option>))}</select></div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Do you have pets?</label>
                <div style={{ display:'flex', gap:16 }}>
                  <label><input type="radio" name="has_pets" checked={form.has_pets==='no'} onChange={()=>update('has_pets','no')} /> No pets</label>
                  <label><input type="radio" name="has_pets" checked={form.has_pets==='yes'} onChange={()=>update('has_pets','yes')} /> Yes, I have pets</label>
                </div>
                {form.has_pets === 'yes' && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display:'block', marginBottom: 6 }}>Describe your pet(s)</label>
                    <textarea className="form-input" rows="2" value={form.pet_description} onChange={(e)=>update('pet_description', e.target.value)} placeholder="Breed, size, age, number of pets, etc." />
                  </div>
                )}
              </div>

              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Do you have vehicles for parking?</label>
                <div style={{ display:'flex', gap:16, marginBottom: 8 }}>
                  <label><input type="radio" name="has_vehicles" checked={form.has_vehicles==='no'} onChange={()=>update('has_vehicles','no')} /> No vehicles</label>
                  <label><input type="radio" name="has_vehicles" checked={form.has_vehicles==='yes'} onChange={()=>update('has_vehicles','yes')} /> Yes, I have vehicles</label>
                </div>
                {form.has_vehicles === 'yes' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:12, background:'#f8f9fa', border:'1px solid #e9ecef', borderRadius:8, padding:12, width:'100%', boxSizing:'border-box', overflow:'hidden' }}>
                    {form.vehicles.map((v, idx) => (
                      <div key={idx} style={{ display:'grid', gridTemplateColumns:'repeat(5, minmax(120px, 1fr)) 100px', gap:12, alignItems:'end', width:'100%', boxSizing:'border-box' }}>
                        <div>
                          <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>License Plate</label>
                          <input className="form-input" value={v.license_plate} onChange={(e)=>{
                            const next = [...form.vehicles];
                            next[idx] = { ...next[idx], license_plate: e.target.value };
                            update('vehicles', next);
                          }} placeholder="ABC-1234" />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Make</label>
                          <input className="form-input" value={v.make} onChange={(e)=>{
                            const next = [...form.vehicles];
                            next[idx] = { ...next[idx], make: e.target.value };
                            update('vehicles', next);
                          }} placeholder="Toyota" />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Model</label>
                          <input className="form-input" value={v.model} onChange={(e)=>{
                            const next = [...form.vehicles];
                            next[idx] = { ...next[idx], model: e.target.value };
                            update('vehicles', next);
                          }} placeholder="Camry" />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Color</label>
                          <input className="form-input" value={v.color} onChange={(e)=>{
                            const next = [...form.vehicles];
                            next[idx] = { ...next[idx], color: e.target.value };
                            update('vehicles', next);
                          }} placeholder="Blue" />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color:'#6c757d', display:'block', marginBottom: 6 }}>Year</label>
                          <input className="form-input" type="number" value={v.year} onChange={(e)=>{
                            const next = [...form.vehicles];
                            next[idx] = { ...next[idx], year: e.target.value };
                            update('vehicles', next);
                          }} placeholder="2020" />
                        </div>
                        <div>
                          {form.vehicles.length > 1 && (
                            <button className="back-button" onClick={()=>{
                              const next = form.vehicles.filter((_,i)=>i!==idx);
                              update('vehicles', next);
                            }}>Remove</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div>
                      <button className="save-button" onClick={()=>update('vehicles', [...form.vehicles, { license_plate:'', make:'', model:'', color:'', year:'' }])}>+ Add another vehicle</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Step 3: Financial & Employment */}
        {step === 3 && (
          <section className="info-section">
            <h2>Financial & Employment Information</h2>
            <div className="form-grid">
              <div className="form-field"><label>Monthly Gross Income</label><input className="form-input" type="number" value={form.monthly_income} onChange={e=>update('monthly_income',e.target.value)} placeholder="$0.00"/></div>
              <div className="form-field"><label>Employment Status</label><select className="form-input" value={form.employment_status} onChange={e=>update('employment_status',e.target.value)}><option value="">Select employment status</option><option value="Employed">Employed</option><option value="Self-employed">Self-employed</option><option value="Student">Student</option><option value="Unemployed">Unemployed</option><option value="Retired">Retired</option></select></div>
              <div className="form-field"><label>Employer Name</label><input className="form-input" value={form.employer_name} onChange={e=>update('employer_name',e.target.value)} /></div>
              <div className="form-field"><label>Job Title</label><input className="form-input" value={form.job_title} onChange={e=>update('job_title',e.target.value)} /></div>
              <div className="form-field"><label>Length of Employment</label><select className="form-input" value={form.employment_length} onChange={e=>update('employment_length',e.target.value)}><option value="">Select employment length</option><option value="<1 year">Less than 1 year</option><option value="1-2 years">1-2 years</option><option value="3-5 years">3-5 years</option><option value=">5 years">More than 5 years</option></select></div>
              <div className="form-field"><label>Supervisor Name</label><input className="form-input" value={form.supervisor_name} onChange={e=>update('supervisor_name',e.target.value)} /></div>
              <div className="form-field"><label>Supervisor Phone</label><input className="form-input" value={form.supervisor_phone} onChange={e=>update('supervisor_phone',e.target.value)} placeholder="(555) 123-4567"/></div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Employment Verification</label>
                <input className="form-input" value={form.employment_verification} onChange={e=>update('employment_verification',e.target.value)} placeholder="Employer HR verification line or contact" />
              </div>
              <div className="form-field" style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                <h3 style={{ margin: '8px 0' }}>Required Documents</h3>
                <div style={{ background:'#f8f9fa', border:'1px solid #e9ecef', borderRadius: 10, padding: 16 }}>
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    <div style={{ background:'#fff', border:'1px dashed #ced4da', borderRadius: 10, padding: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Most Recent Paystub (PDF or Image)</div>
                      <label htmlFor="doc-paystub" style={{ display:'inline-block', background:'#0d6efd', color:'#fff', padding:'8px 12px', borderRadius: 6, cursor:'pointer', boxShadow:'0 1px 3px rgba(13,110,253,0.35)' }}>Choose File</label>
                      <input id="doc-paystub" type="file" accept="application/pdf,image/*" style={{ display:'none' }} onChange={(e)=>setPaystubFile(e.target.files && e.target.files[0])} />
                      <div style={{ fontSize: 12, color:'#6c757d', marginTop: 8 }}>{paystubFile ? paystubFile.name : 'No file chosen'}</div>
                    </div>
                    <div style={{ background:'#fff', border:'1px dashed #ced4da', borderRadius: 10, padding: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Most Recent Bank Statement (PDF or Image)</div>
                      <label htmlFor="doc-bank" style={{ display:'inline-block', background:'#0d6efd', color:'#fff', padding:'8px 12px', borderRadius: 6, cursor:'pointer', boxShadow:'0 1px 3px rgba(13,110,253,0.35)' }}>Choose File</label>
                      <input id="doc-bank" type="file" accept="application/pdf,image/*" style={{ display:'none' }} onChange={(e)=>setBankStatementFile(e.target.files && e.target.files[0])} />
                      <div style={{ fontSize: 12, color:'#6c757d', marginTop: 8 }}>{bankStatementFile ? bankStatementFile.name : 'No file chosen'}</div>
                    </div>
                    <div style={{ background:'#fff', border:'1px dashed #ced4da', borderRadius: 10, padding: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Credit Report (PDF or Image)</div>
                      <label htmlFor="doc-credit" style={{ display:'inline-block', background:'#0d6efd', color:'#fff', padding:'8px 12px', borderRadius: 6, cursor:'pointer', boxShadow:'0 1px 3px rgba(13,110,253,0.35)' }}>Choose File</label>
                      <input id="doc-credit" type="file" accept="application/pdf,image/*" style={{ display:'none' }} onChange={(e)=>setCreditReportFile(e.target.files && e.target.files[0])} />
                      <div style={{ fontSize: 12, color:'#6c757d', marginTop: 8 }}>{creditReportFile ? creditReportFile.name : 'No file chosen'}</div>
                    </div>
                  </div>
                  <div style={{ color:'#6c757d', fontSize: 12, marginTop: 10 }}>
                    These uploads are required to continue.
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 4: References */}
        {step === 4 && (
          <section className="info-section">
            <h2>References</h2>
            <div className="form-grid">
              <div className="form-field"><label>Reference 1 - Full Name</label><input className="form-input" value={form.reference1_name} onChange={e=>update('reference1_name',e.target.value)} /></div>
              <div className="form-field"><label>Reference 1 - Phone Number</label><input className="form-input" value={form.reference1_phone} onChange={e=>update('reference1_phone',e.target.value)} placeholder="(555) 123-4567"/></div>
              <div className="form-field"><label>Reference 1 - Relationship</label><input className="form-input" value={form.reference1_relationship} onChange={e=>update('reference1_relationship',e.target.value)} placeholder="Friend, coworker, etc."/></div>
              <div className="form-field"><label>Reference 2 - Full Name</label><input className="form-input" value={form.reference2_name} onChange={e=>update('reference2_name',e.target.value)} /></div>
              <div className="form-field"><label>Reference 2 - Phone Number</label><input className="form-input" value={form.reference2_phone} onChange={e=>update('reference2_phone',e.target.value)} /></div>
              <div className="form-field"><label>Reference 2 - Relationship</label><input className="form-input" value={form.reference2_relationship} onChange={e=>update('reference2_relationship',e.target.value)} /></div>
            </div>
          </section>
        )}

        {/* Step 5: Previous Rental History & Legal */}
        {step === 5 && (
          <section className="info-section">
            <h2>Previous Rental History</h2>
            <div className="form-grid">
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Previous Address</label>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input className="form-input" placeholder="Street Address" value={form.prev_street} onChange={e=>update('prev_street',e.target.value)} />
                  <input className="form-input" placeholder="Apt / Unit (optional)" value={form.prev_apt} onChange={e=>update('prev_apt',e.target.value)} />
                  <input className="form-input" placeholder="City / Town" value={form.prev_city} onChange={e=>update('prev_city',e.target.value)} />
                  <select className="form-input" value={form.prev_country} onChange={e=>update('prev_country',e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {(form.prev_country === 'United States' || form.prev_country === 'Canada') && (
                    <input className="form-input" placeholder={form.prev_country === 'Canada' ? 'Province' : 'State'} value={form.prev_state} onChange={e=>update('prev_state',e.target.value)} />
                  )}
                  <input className="form-input" placeholder="Postal / ZIP Code" value={form.prev_postal} onChange={e=>update('prev_postal',e.target.value)} />
                </div>
              </div>
              <div className="form-field"><label>Previous Landlord Name</label><input className="form-input" value={form.prev_landlord_name} onChange={e=>update('prev_landlord_name',e.target.value)} /></div>
              <div className="form-field"><label>Previous Landlord Phone</label><input className="form-input" value={form.prev_landlord_phone} onChange={e=>update('prev_landlord_phone',e.target.value)} placeholder="(555) 123-4567"/></div>
              <div className="form-field"><label>Previous Monthly Rent</label><input className="form-input" type="number" value={form.prev_monthly_rent} onChange={e=>update('prev_monthly_rent',e.target.value)} placeholder="$0.00"/></div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}><label>Reason for Leaving</label><textarea className="form-input" rows="2" value={form.reason_for_leaving} onChange={e=>update('reason_for_leaving',e.target.value)} placeholder="Please explain why you are leaving your current residence"/></div>
              <div className="form-field" style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                <h3 style={{ margin: '8px 0' }}>Legal History</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label>Have you ever been convicted of a felony?</label>
                    <div style={{ display:'flex', gap:16 }}>
                      <label><input type="radio" name="felony" checked={form.felony==='no'} onChange={()=>update('felony','no')} /> No</label>
                      <label><input type="radio" name="felony" checked={form.felony==='yes'} onChange={()=>update('felony','yes')} /> Yes</label>
                    </div>
                  </div>
                  <div>
                    <label>Have you ever been evicted from a rental property?</label>
                    <div style={{ display:'flex', gap:16 }}>
                      <label><input type="radio" name="eviction" checked={form.eviction==='no'} onChange={()=>update('eviction','no')} /> No</label>
                      <label><input type="radio" name="eviction" checked={form.eviction==='yes'} onChange={()=>update('eviction','yes')} /> Yes</label>
                    </div>
                  </div>
                  <div>
                    <label>Have you ever filed for bankruptcy?</label>
                    <div style={{ display:'flex', gap:16 }}>
                      <label><input type="radio" name="bankruptcy" checked={form.bankruptcy==='no'} onChange={()=>update('bankruptcy','no')} /> No</label>
                      <label><input type="radio" name="bankruptcy" checked={form.bankruptcy==='yes'} onChange={()=>update('bankruptcy','yes')} /> Yes</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}><label>Additional Information</label><textarea className="form-input" rows="3" value={form.additional_info} onChange={e=>update('additional_info',e.target.value)} placeholder="Optional: Any additional information that may help with your application"/></div>
            </div>
          </section>
        )}

        {/* Step 6: Review & Submit */}
        {step === 6 && (
          <section className="info-section">
            <h2>Review & Submit Application</h2>
            <div style={{ background:'#f8f9fa', border:'1px solid #e9ecef', borderRadius:8, padding:16, marginBottom:16 }}>
              <div>Name: {form.first_name} {form.last_name}</div>
              <div>Email: {form.email}</div>
              <div>Phone: {form.phone}</div>
              <div>Property: {form.property_interest || '—'}</div>
              <div>Move-in: {form.move_in_date || '—'}</div>
              <div>Monthly Income: {form.monthly_income || '—'}</div>
              {form.co_applicants && form.co_applicants.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Additional Occupants</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {form.co_applicants.map((o, i) => (
                      <li key={i}>{o.first_name} {o.last_name} — {o.status || 'occupant'} {o.email ? `(${o.email})` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div style={{ display:'grid', gap:12 }}>
              <label style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                <input type="checkbox" />
                <span>Background Check Authorization — I authorize the landlord to conduct a background check, credit check, and verify employment and rental history as part of my rental application.</span>
              </label>
              <label style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                <input type="checkbox" />
                <span>Terms and Conditions — I certify that all information provided in this application is true and complete.</span>
              </label>
            </div>
          </section>
        )}

        {/* Navigation */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
          <button className="back-button" disabled={step===1 || saving} onClick={()=>setStep(step-1)}>Previous</button>
          {step < 6 ? (
            <button className="save-button" disabled={!canNext || saving} onClick={()=>setStep(step+1)}>{saving ? 'Saving...' : 'Next Step'}</button>
          ) : (
            <button className="save-button" disabled={saving} onClick={handleSubmit}>{saving ? 'Submitting...' : 'Submit Application'}</button>
          )}
        </div>
      </div>
    </div>
  );
}


